import {layoutActions} from 'features/layout/redux';
import {call, put, select} from 'redux-saga/effects';
import i18n from 'i18n';
import {asyncSagaConstructor, getErrors} from 'redux/common';
import api from 'api';
import ApiService from 'api/ApiService';
import {DiplomatFeature} from 'types/DiplomatFeature';
import {licenseSelectors} from 'redux/license';
import {backupTypes} from 'redux/backuprestore';
import {waitUntilAllJobsStopped} from '../waitUntilAllJobsStopped';
import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {DialogTypes} from 'common/names';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';

let i = 0;
const lockMsg = i++,
   suspendMsg = i++,
   stoppedMsg = i++,
   backupMsg = i++,
   mergeMsg = i++,
   doneMsg = i++,
   unsuspendMsg = i++,
   unlockMsg = i++;
let lines: string[];
export function* initMergeProcess(
   backupDirPath: string,
   password: string,
   overwrite: boolean,
   disconnect = false
): Generator<any> {
   let buttonLabel = i18n.t('Cancel');
   lines = [];
   const isFeatureSupported = (yield select(licenseSelectors.isFeatureSupported)) as ReturnType<
      typeof licenseSelectors.isFeatureSupported
   >;
   let retryRequired = false;

   try {
      lines[lockMsg] = i18n.t('Locking server...');
      yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

      //lock the server to new logins
      yield call(
         asyncSagaConstructor(api.lockformerge, backupTypes.LOCK_FOR_MERGE, {
            getMethod: ApiService.postSecureJson,
         })
      );
      lines[lockMsg] = i18n.t('Server has been locked');
      if (overwrite) {
         lines[suspendMsg] = i18n.t('Suspending job scheduling...');
      } else {
         lines[backupMsg] = i18n.t('Backing up current transaction datastore...');
      }
      yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

      if (overwrite) {
         //suspend all jobs
         yield call(
            asyncSagaConstructor(api.suspendRestore, backupTypes.SUSPEND_RESTORE, {
               getMethod: ApiService.postSecureJson,
            })
         );
         lines[suspendMsg] = i18n.t('Job scheduling has been suspended');
         lines[stoppedMsg] = i18n.t('Waiting for current jobs to stop...');
         yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

         //wait for running jobs to finish
         yield call(waitUntilAllJobsStopped);
         lines[stoppedMsg] = i18n.t('All jobs have stopped');
         lines[backupMsg] = i18n.t('Backing up current transaction datastore...');
         yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));
      }

      const {result: path} = (yield call(
         asyncSagaConstructor(api.backup, backupTypes.BACKUP, {
            getMethod: ApiService.postSecureJson,
            data: {},
         })
      )) as {result: string};
      lines[backupMsg] = i18n.t('Transaction datastore backed up to \n {{path}}', {path});
      lines[mergeMsg] = i18n.t('Merging transaction datastore...');
      yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

      yield call(
         asyncSagaConstructor(api.merge, backupTypes.MERGE, {
            getMethod: ApiService.postSecureJson,
            data: {
               disconnect,
               backupDirPath,
               password,
               suspend: true,
               overwrite,
            },
            hideErrorToast: true,
         })
      );
      lines[mergeMsg] = i18n.t('Transaction datastore Merged');
      lines[doneMsg] = i18n.t('Merge Complete');
      yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));
      yield call(cleanup, overwrite);
      yield put(layoutActions.loadTreeNav());
   } catch (e) {
      if ((e as Response).status === 409) {
         const dialogResult = (yield call(openDialogWithResult, {
            id: DialogTypes.ConfirmDialog.id,
            data: {
               confirmLabel: i18n.t('Disconnect'),
               title: i18n.t('Current Connections'),
               content: i18n.t(
                  'You cannot complete a merge when other users are connected.  Select "Disconnect" to\n' +
                     'disconnect all users shown below and continue. Select "Cancel" to cancel the merge.'
               ),
            },
         })) as ICloseSubDialogAction<boolean>;

         if (dialogResult.payload?.data) {
            retryRequired = true;
         }
      } else {
         yield call(handleError, e);
      }
   } finally {
      try {
         //if we are in a mode that does not allow for unsuspending jobs
         //from the menu make sure that jobs are unsuspended when we leave
         if (overwrite && !isFeatureSupported(DiplomatFeature.JOB_SUSPENSION)) {
            //unsuspend all jobs
            yield call(
               asyncSagaConstructor(api.releaseRestore, backupTypes.RELEASE_RESTORE, {
                  getMethod: ApiService.postSecureJson,
               })
            );
            lines[unsuspendMsg] = i18n.t('Job scheduling has been unsuspended');
            yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));
         }

         yield call(
            asyncSagaConstructor(api.unlock, backupTypes.UNLOCK, {
               getMethod: ApiService.postSecureJson,
            })
         );
         lines[unlockMsg] = i18n.t('Server has been unlocked');
         buttonLabel = i18n.t('Close');
         yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));

         if (retryRequired) {
            yield call(initMergeProcess, backupDirPath, password, overwrite, true);
            retryRequired = false;
         }
      } catch (e) {
         yield call(handleError, e);
      }
   }
}

function* handleError(e: any) {
   const {message} = yield call(getErrors, e, '');
   lines[doneMsg] = `${message}\n ${i18n.t('Merge process has been cancelled.')}`;
   const buttonLabel = i18n.t('Close');
   yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));
}

function* cleanup(overwrite: boolean) {
   const buttonLabel = i18n.t('Close');

   const isFeatureSupported = (yield select(licenseSelectors.isFeatureSupported)) as ReturnType<
      typeof licenseSelectors.isFeatureSupported
   >;

   //if we are in a mode that allows for unsuspending jobs from the
   //menu see if user wants to unsuspend jobs now
   if (overwrite && isFeatureSupported(DiplomatFeature.JOB_SUSPENSION)) {
      const dialogResult = (yield call(openDialogWithResult, {
         id: DialogTypes.ConfirmDialog.id,
         data: {
            confirmLabel: i18n.t('Yes'),
            cancelLabel: i18n.t('No'),
            title: i18n.t('Release Job Suspension'),
            content: i18n.t(
               'Do you want to release the DB merge job suspension?\n' +
                  "NOTE: Selecting 'Yes' will not release transactions that were already suspended before\n" +
                  'the merge operation and newly merged transactions will  remain individually suspended.'
            ),
         },
      })) as ICloseSubDialogAction<boolean>;

      if (dialogResult.payload?.data) {
         yield call(
            asyncSagaConstructor(api.releaseRestore, backupTypes.RELEASE_RESTORE, {
               getMethod: ApiService.postSecureJson,
            })
         );
         lines[unsuspendMsg] = i18n.t('Job scheduling has been unsuspended');
         yield put(layoutActions.setActiveDialogStatus(lines, buttonLabel));
      }
   }
}
